// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { ResponsiveBar } from '@nivo/bar';
import {
  getPerformanceLineLabel,
  getTranslatedChartLabel,
  type PerformanceLineChartFeature,
} from '@shared/ui-kit/features/performance-line-chart';
import { ChartContainer, Legend } from '@shared/ui-kit/components';
import { useCallback, useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';

export interface ReturnsChartProps {
  readonly data: {
    label: string;
    portfolio: number;
    benchmark: number;
  }[];
  readonly feature?: PerformanceLineChartFeature;
}

export const ReturnsChart: FC<ReturnsChartProps> = ({ data, feature }) => {
  const { t } = useTranslation();
  const hasData = useMemo(() => data.length > 0, [data]);
  const BENCHMARK_COLOR = '#79828D80';
  const PORTFOLIO_COLOR = '#1072BA80';
  const getTranslatedLabel = useCallback(
    (id: string): string => {
      const transformedId = getPerformanceLineLabel(id, feature as string);

      return getTranslatedChartLabel(transformedId, t, feature);
    },
    [t, feature],
  );

  return (
    <ChartContainer
      header={t('research.returns-pct', { ns: 'research' })}
      data-testid="returns-chart"
    >
      {hasData ? (
        <>
          <div className="flex flex-wrap justify-end gap-x-7 gap-y-2">
            <Legend
              label={getTranslatedLabel('BENCHMARK')}
              color={BENCHMARK_COLOR}
            />
            <Legend
              label={getTranslatedLabel('PORTFOLIO')}
              color={PORTFOLIO_COLOR}
            />
          </div>
          <ResponsiveBar
            data={data.filter(item => item.portfolio !== 0 && item.benchmark !== 0)}
            keys={['portfolio', 'benchmark']}
            indexBy="label"
            margin={{ top: 50, right: 60, bottom: 50, left: 60 }}
            padding={0.2}
            colors={({ id }) => {
              const normalizedId = id.toString().toUpperCase();

              if (normalizedId.includes('BENCHMARK')) {
                return BENCHMARK_COLOR;
              }

              if (normalizedId.includes('PORTFOLIO')) {
                return PORTFOLIO_COLOR;
              }

              return BENCHMARK_COLOR;
            }}
            axisLeft={{ format: ' >-.2%' }}
            axisBottom={{ tickPadding: 24 }}
            valueFormat=" >-.2%"
            animate={false}
            groupMode="grouped"
            labelSkipHeight={12}
            theme={{
              axis: {
                ticks: {
                  text: {
                    fontSize: 11,
                    fontFamily: 'poppins',
                    fill: 'var(--blueGrayMuted)',
                    textTransform: 'uppercase',
                  },
                },
              },
              labels: {
                text: {
                  fontFamily: 'poppins',
                  fill: 'var(--white)',
                  textTransform: 'uppercase',
                },
              },
            }}
            tooltipLabel={
              /* istanbul ignore next */ ({ id }) =>
                getPerformanceLineLabel(
                  id.toString().toUpperCase(),
                  feature as string,
                )
            }
          />
        </>
      ) : (
        <div
          className="mx-auto mt-5 space-y-4 px-8 text-center text-border-dark"
          data-testid="no-data-message"
        >
          <p>No data available</p>
        </div>
      )}
    </ChartContainer>
  );
};
